import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "../lib/api";
import Sidebar from "../components/Sidebar";
import HeroSlider from "../components/HeroSlider";
import { GAMES } from "../constants/loadout";
import LastPostMember from "../components/LastPostMember";
import ForumStats from "../components/ForumStats";
import ForumCategoryList from "../components/ForumCategoryList";
import WidgetZone from "../components/WidgetZone";
import { useT } from "../i18n";
import * as Icons from "lucide-react";
import { ChevronRight, MessageSquare, FileText, Megaphone, Target, Monitor, Crosshair, Diamond, Headphones, Cpu, Settings, Folder, LayoutGrid } from "lucide-react";

function getCategoryIconAndDesc(id) {
  const norm = (id || "").toLowerCase();
  if (norm.includes("anun") || norm.includes("importan")) return { icon: Megaphone, defaultDesc: "Noutăți importante, update-uri și anunțuri oficiale", fallbackColor: "#ffaa00" };
  if (norm.includes("cs16") || norm.includes("1.6")) return { icon: Target, defaultDesc: "Servere, pluginuri, addons, resurse și discuții CS 1.6", fallbackColor: "#00F0FF" };
  if (norm.includes("cs2") || norm.includes("counter-strike 2")) return { icon: Monitor, defaultDesc: "Discuții, ghiduri, demo-uri și competiții CS 2", fallbackColor: "#FF4F00" };
  if (norm.includes("pubg")) return { icon: Crosshair, defaultDesc: "Turnee, scrims, rezultate, echipe și organizare competiții", fallbackColor: "#ff2a2a" };
  if (norm.includes("vip")) return { icon: Diamond, defaultDesc: "Beneficii premium, shop, rank-uri speciale și acces exclusiv", fallbackColor: "#a200ff" };
  if (norm.includes("support") || norm.includes("suport")) return { icon: Headphones, defaultDesc: "Cereri, reclamații, asistență și contact cu staff-ul comunității", fallbackColor: "#39ff14" };
  if (norm.includes("comunitat")) return { icon: Cpu, defaultDesc: "Discuții libere și interacțiune între membri", fallbackColor: "#e4e4e7" };
  if (norm.includes("admin")) return { icon: Settings, defaultDesc: "Management, dezvoltare și administrarea comunității", fallbackColor: "#a1a1aa" };
  return { icon: LayoutGrid, defaultDesc: "Subiecte și discuții aferente categoriei", fallbackColor: "#a1a1aa" };
}

const GRID_CLASS = {
  1: "grid-cols-1",
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
  4: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4",
  5: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5",
};

export default function Forums() {
  const t = useT();
  const [collapsedCategories, setCollapsedCategories] = useState({});

  const toggleCategory = (id) => {
    setCollapsedCategories(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const { data: boardsData, isLoading: isLoadingBoards } = useQuery({
    queryKey: ["boards"],
    queryFn: async () => {
      const res = await api.get("/boards");
      return res.data;
    },
    staleTime: 60000,
  });

  const { data: settingsData } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => {
      const res = await api.get("/settings");
      return res.data;
    },
    staleTime: 60000,
  });

  const boards = boardsData || [];
  const settings = settingsData || { slides: [], subforum_columns: 2 };

  const subCols = Math.max(1, Math.min(5, settings.subforum_columns || 2));

  const categoriesList = settings.categories?.length > 0 ? settings.categories : GAMES;

  // Group by game dynamically to include all categories even if not in categoriesList
  const gamesWithBoards = (() => {
    const grouped = {};
    boards.forEach((b) => {
      if (b.parent_id) return;
      const gId = b.game || "general";
      if (!grouped[gId]) {
        const found = categoriesList.find(x => x.id === gId);
        grouped[gId] = found || { id: gId, label: gId.toUpperCase() };
      }
    });
    return Object.values(grouped).sort((a, b) => {
      const idxA = categoriesList.findIndex(x => x.id === a.id);
      const idxB = categoriesList.findIndex(x => x.id === b.id);
      if (idxA !== -1 && idxB !== -1) return idxA - idxB;
      if (idxA !== -1) return -1;
      if (idxB !== -1) return 1;
      return a.label.localeCompare(b.label);
    });
  })();

  return (
    <div className="space-y-6 idg-forums-page" data-testid="forums-page">
      <HeroSlider slides={settings.slides || []} />
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3 space-y-6">
          <WidgetZone position="above_boards" />
          <header className="tf-slide-up">
            <h1 className="tf-heading text-4xl font-bold uppercase tracking-widest">{t("forums.title")}</h1>
            <p className="text-zinc-500 tf-mono text-sm mt-1">{t("forums.subtitle")}</p>
          </header>

          <ForumCategoryList boards={boards} isLoading={isLoadingBoards} settings={settings} categoriesList={categoriesList} />

          <WidgetZone position="below_boards" />
          <ForumStats />
        </div>
        <div className="lg:col-span-1">
          <Sidebar />
        </div>
      </div>
    </div>
  );
}
